import { useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CBadge, CButton, CButtonGroup, CCard, CSpinner } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCart } from "@coreui/icons";
import { RiskIcon } from "@scryerlabs/ui";
import type {
  PortfolioBuilderDto,
  PortfolioDto,
  PortfolioSide,
} from "../../../api/hooks/usePortfolioBuilder";
import { useReferenceValueLabel, useRiskProfiles } from "../../../api/hooks/useReference";
import {
  customerHasActiveSubscription,
  useMySubscriptions,
} from "../../../api/hooks/useStrategySubscriptions";
import { isIncludedInMembership, useMyMemberships } from "../../../api/hooks/useMemberships";
import type { BillingCycle } from "../../../api/hooks/useTradingStrategies";
import { useCartStore, type StrategyCartItem } from "../../../state/cartStore";
import { useToast } from "../../../context/useToast";
import { formatMonthYear } from "../../../lib/dates";
import { formatPriceUsd } from "../../../lib/money";
import { displayTicker } from "../../trading-strategies/tickers";
import {
  FIGURE,
  directionOf,
  formatAvgYear,
  formatDrawdown,
  formatSignedOnePlace,
  strategySide,
  tradesPhrase,
} from "../portfolio-builder";
import { PortfolioCartHoldsUpgradeDialog } from "./PortfolioCartHoldsUpgradeDialog";

const BILLING_CYCLES: BillingCycle[] = ["Monthly", "Yearly"];

interface PortfolioPanelProps {
  builder: PortfolioBuilderDto;
  side: PortfolioSide;
  portfolio: PortfolioDto;
  /** What the portfolio is to the customer: their match, a step on their path, or a dot they tapped. */
  eyebrow: string;
  isMatch: boolean;
  onBackToMatch: () => void;
  /** Not the portfolio for the latest answers or tap, so it is dimmed and can't be added. */
  stale: boolean;
  /** The portfolio that replaces it is on its way. */
  loading: boolean;
  /** The what-if growth, which the panel shows between the figures and the strategies. */
  whatIf: ReactNode;
}

/** The portfolio being looked at: its figures, what money would have done in it, its strategies and the buy. */
export function PortfolioPanel({
  builder,
  side,
  portfolio,
  eyebrow,
  isMatch,
  onBackToMatch,
  stale,
  loading,
  whatIf,
}: PortfolioPanelProps) {
  const { t } = useTranslation(["portfolioBuilder", "tradingStrategies"]);
  const riskLabel = useReferenceValueLabel(useRiskProfiles().data);
  const [cycle, setCycle] = useState<BillingCycle>("Monthly");
  const [heldBack, setHeldBack] = useState<StrategyCartItem[] | null>(null);
  const strategyItems = useCartStore((s) => s.strategyItems);
  const cartUpgrade = useCartStore((s) => s.upgrade);
  const cartExtension = useCartStore((s) => s.extension);
  const addStrategy = useCartStore((s) => s.addStrategy);
  const { showSuccess } = useToast();
  const subscriptions = useMySubscriptions();
  const memberships = useMyMemberships();

  const holdingsUnknown =
    subscriptions.isLoading ||
    Boolean(subscriptions.error) ||
    memberships.isLoading ||
    Boolean(memberships.error);

  const strategiesByKey = new Map(builder.strategies.map((strategy) => [strategy.key, strategy]));
  const rows = portfolio.strategies.flatMap((key) => {
    const strategy = strategiesByKey.get(key);
    const sold = strategy ? strategySide(strategy, side) : null;
    if (!strategy || !sold || strategy.name === null) return [];

    const inCart = strategyItems.some((item) => item.strategyId === sold.strategyId);
    const held =
      customerHasActiveSubscription(sold.strategyId, subscriptions.data ?? []) ||
      isIncludedInMembership(sold.strategyId, memberships.data ?? []);
    const priceUsd = cycle === "Monthly" ? sold.monthlyPriceUsd : sold.yearlyPriceUsd;

    return [{ strategy, name: strategy.name, sold, inCart, held, priceUsd }];
  });

  // A strategy unpublished after its revision went live keeps its place in the portfolio's figures,
  // so the customer is told rather than shown fewer strategies than the figures describe.
  const notOffered = portfolio.strategies.length - rows.length;
  const totalUsd = rows.reduce((sum, row) => sum + row.priceUsd, 0);
  const onePrice = rows.every((row) => row.priceUsd === rows[0]?.priceUsd);
  const toAdd = rows.filter((row) => !row.inCart && !row.held);
  const allHeld = rows.length > 0 && rows.every((row) => row.held);

  const addToCart = () => {
    const items: StrategyCartItem[] = toAdd.map((row) => ({
      strategyId: row.sold.strategyId,
      name: row.name,
      billingCycle: cycle,
      priceUsd: row.priceUsd,
    }));

    if (cartUpgrade !== null || cartExtension !== null) {
      setHeldBack(items);
      return;
    }

    items.forEach((item) => addStrategy(item));
    showSuccess(t("panel.added", { count: items.length }));
  };

  const figure = (key: string) => portfolio.figures[key] ?? null;
  const avgYear = figure(FIGURE.avgYear);
  const realDd = figure(FIGURE.realDd);
  const tradesPerYear = figure(FIGURE.tradesPerYear);
  const worstMonth = figure(FIGURE.worstMonth);
  const winMonths = figure(FIGURE.winMonths);
  const winRate = figure(FIGURE.winRate);
  const trades = tradesPerYear === null ? null : tradesPhrase(tradesPerYear);
  const period = `${formatMonthYear(builder.backtest.from)} – ${formatMonthYear(builder.backtest.to)}`;
  const share = `${Number((100 / portfolio.size).toFixed(1))}%`;

  const tiles = [
    {
      key: "avgYear",
      value: avgYear === null ? "–" : formatAvgYear(avgYear),
      tone: avgYear !== null && avgYear < 0 ? "text-danger" : "text-success",
    },
    { key: "realDd", value: realDd === null ? "–" : formatDrawdown(realDd), tone: "text-danger" },
    {
      key: "trades",
      value: trades === null ? "–" : t(`panel.trades.${trades.key}`, { count: trades.count }),
      tone: "is-words",
    },
    {
      key: "worstMonth",
      value: worstMonth === null ? "–" : formatSignedOnePlace(worstMonth),
      tone: worstMonth !== null && worstMonth < 0 ? "text-danger" : "text-success",
    },
    { key: "monthsUp", value: winMonths === null ? "–" : `${Math.round(winMonths)}%`, tone: "" },
    { key: "winRate", value: winRate === null ? "–" : `${Math.round(winRate)}%`, tone: "" },
  ];

  const addLabel =
    toAdd.length > 0
      ? t("panel.add", { count: toAdd.length })
      : allHeld
        ? t("panel.allHeld")
        : t("panel.allInCart");

  return (
    <CCard className={`portfolio-panel${stale ? " is-stale" : ""}`} aria-busy={loading}>
      {loading && (
        <div className="portfolio-panel-loading">
          <CSpinner size="sm" color="primary" />
        </div>
      )}
      <section>
        <div className="portfolio-panel-head">
          <span className="portfolio-eyebrow">{eyebrow}</span>
          {!isMatch && (
            <CButton size="sm" color="primary" variant="outline" onClick={onBackToMatch}>
              {t("panel.backToMatch")}
            </CButton>
          )}
        </div>
        <h2 className="portfolio-panel-title">
          {t("panel.title", {
            count: portfolio.size,
            side: t(`tradingStrategies:sides.${directionOf(side)}`),
          })}
        </h2>
        <p className="portfolio-panel-split small text-body-secondary mb-0">{t("panel.split", { share })}</p>
        <div className="portfolio-tiles">
          {tiles.map((tile) => (
            <div key={tile.key} className="portfolio-tile">
              <div className={`portfolio-tile-value ${tile.tone}`}>{tile.value}</div>
              <div className="portfolio-tile-label">{t(`panel.figures.${tile.key}`)}</div>
            </div>
          ))}
        </div>
      </section>

      {whatIf}

      <section>
        <span className="portfolio-eyebrow">{t("panel.strategies")}</span>
        <ul className="portfolio-strategies">
          {rows.map(({ strategy, name, sold, inCart, held }) => {
            const ownAvgYear = sold.figures[FIGURE.avgYear] ?? null;
            const ownRealDd = sold.figures[FIGURE.realDd] ?? null;
            const risk = riskLabel(strategy.riskProfile);

            return (
              <li key={strategy.key} className="portfolio-strategy">
                <span className="portfolio-ticker">{displayTicker(strategy.symbol)}</span>
                <div className="portfolio-strategy-body">
                  <div className="portfolio-strategy-name">{name}</div>
                  <div className="portfolio-strategy-sub">
                    <span className="portfolio-strategy-risk">
                      <RiskIcon
                        riskProfile={strategy.riskProfile ?? 0}
                        label={t("tradingStrategies:traits.risk", { level: risk })}
                        size={13}
                      />
                      {risk}
                    </span>
                    {ownAvgYear !== null && ownRealDd !== null && (
                      <span>
                        <Trans
                          t={t}
                          i18nKey="panel.onItsOwn"
                          values={{ avgYear: formatAvgYear(ownAvgYear), drawdown: formatDrawdown(ownRealDd) }}
                          components={{ gain: <span className="text-success" />, fall: <span className="text-danger" /> }}
                        />
                      </span>
                    )}
                  </div>
                </div>
                {inCart ? (
                  <CBadge color="success" shape="rounded-pill">
                    {t("panel.inCart")}
                  </CBadge>
                ) : held ? (
                  <CBadge color="success" shape="rounded-pill">
                    {t("panel.held")}
                  </CBadge>
                ) : null}
              </li>
            );
          })}
        </ul>
        {notOffered > 0 && (
          <p className="portfolio-not-offered small text-body-secondary mb-0">
            {t("panel.notOffered", { count: notOffered })}
          </p>
        )}
      </section>

      <section className="portfolio-buy">
        <div className="portfolio-buy-row">
          <span className="portfolio-total">
            {formatPriceUsd(totalUsd)}{" "}
            <small>
              {cycle === "Monthly" ? t("panel.perMonth") : t("panel.perYearTotal")}
              {onePrice &&
                rows.length > 0 &&
                ` · ${t("panel.breakdown", { count: rows.length, price: formatPriceUsd(rows[0].priceUsd) })}`}
            </small>
          </span>
          <CButtonGroup size="sm" className="btn-group-pill" role="group" aria-label={t("panel.billing")}>
            {BILLING_CYCLES.map((option) => (
              <CButton
                key={option}
                color="primary"
                variant={cycle === option ? undefined : "outline"}
                aria-pressed={cycle === option}
                onClick={() => setCycle(option)}
              >
                {option === "Monthly" ? t("panel.monthly") : t("panel.yearly")}
              </CButton>
            ))}
          </CButtonGroup>
        </div>
        {holdingsUnknown ? (
          <CButton color="secondary" className="w-100 portfolio-add" disabled>
            {t("panel.checkingSubscriptions")}
          </CButton>
        ) : (
          <CButton
            color="primary"
            className="w-100 portfolio-add"
            disabled={stale || toAdd.length === 0}
            onClick={addToCart}
          >
            <CIcon icon={cilCart} className="me-2" />
            {addLabel}
          </CButton>
        )}
        <p className="portfolio-fine mb-0">{t("panel.fine", { period })}</p>
      </section>

      <PortfolioCartHoldsUpgradeDialog items={heldBack} onClose={() => setHeldBack(null)} />
    </CCard>
  );
}
